import { openUrlInNewTab } from '@/config/ipeRoutes';

/** 跨标签页传递 location.state（sessionStorage 不共享，须用 localStorage） */
export const WORKBENCH_HANDOFF_QUERY = 'ipeHandoff';
const STORAGE_PREFIX = 'ipe-wb-handoff:';
const PREFILL_BACKUP_PREFIX = 'ipe-studio-prefill:';
const HANDOFF_TTL_MS = 60_000;
const PREFILL_BACKUP_TTL_MS = 5 * 60_000;

function storageKey(token) {
  return `${STORAGE_PREFIX}${token}`;
}

function prefillBackupKey(navId) {
  return `${PREFILL_BACKUP_PREFIX}${String(navId || '').trim()}`;
}

function makeToken() {
  return `${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
}

function readHandoffPayload(token, { consume = false } = {}) {
  const key = String(token || '').trim();
  if (!key || typeof localStorage === 'undefined') return null;
  try {
    const raw = localStorage.getItem(storageKey(key));
    if (consume) {
      localStorage.removeItem(storageKey(key));
    }
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;
    if (Number(parsed.exp) > 0 && Date.now() > Number(parsed.exp)) return null;
    return parsed.state && typeof parsed.state === 'object' ? parsed.state : {};
  } catch {
    if (consume) {
      try {
        localStorage.removeItem(storageKey(key));
      } catch {
        /* ignore */
      }
    }
    return null;
  }
}

/**
 * @param {Record<string, unknown>} [state]
 * @returns {string} token
 */
export function stashWorkbenchNavHandoff(state = {}) {
  const token = makeToken();
  if (typeof localStorage === 'undefined') return token;
  try {
    localStorage.setItem(
      storageKey(token),
      JSON.stringify({
        state: state && typeof state === 'object' ? state : {},
        exp: Date.now() + HANDOFF_TTL_MS,
      }),
    );
  } catch {
    /* quota / private mode */
  }
  return token;
}

/**
 * 只读 peek：首屏 useState/useMemo 可在 Bootstrap consume 之前同步拿到 cropSources。
 * 不删除 localStorage，仍由 consumeWorkbenchNavHandoff / Bootstrap 消费。
 * @param {string} [search] 默认 window.location.search
 * @returns {Record<string, unknown>|null}
 */
export function peekWorkbenchNavHandoffState(search) {
  if (typeof window === 'undefined' && search == null) return null;
  try {
    const sp = new URLSearchParams(
      search != null ? search : (window.location.search || ''),
    );
    const token = String(sp.get(WORKBENCH_HANDOFF_QUERY) || '').trim();
    if (!token) return null;
    return readHandoffPayload(token, { consume: false });
  } catch {
    return null;
  }
}

/**
 * @param {string} token
 * @returns {Record<string, unknown>|null}
 */
export function consumeWorkbenchNavHandoff(token) {
  return readHandoffPayload(token, { consume: true });
}

function readPrefillBackupRecord(navId) {
  const key = prefillBackupKey(navId);
  if (!key || typeof sessionStorage === 'undefined') return null;
  try {
    const raw = sessionStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;
    if (Number(parsed.exp) > 0 && Date.now() > Number(parsed.exp)) {
      sessionStorage.removeItem(key);
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

/** 规范化备份图片项：模特槽位常用 apiUrl，需兼容为 url */
function normalizeBackupSourceItem(item) {
  if (!item || typeof item !== 'object') return null;
  const id = item.id != null ? String(item.id).trim() : '';
  const url = String(item.url || item.apiUrl || item.previewUrl || item.viewUrl || item.displayUrl || '').trim();
  if (!id || !url) return null;
  return {
    ...item,
    id,
    url,
    name: item.name || id,
    previewUrl: item.previewUrl || item.viewUrl || item.displayUrl || url,
    apiUrl: item.apiUrl || url,
  };
}

function normalizeBackupSources(sources) {
  if (!Array.isArray(sources)) return [];
  return sources.map(normalizeBackupSourceItem).filter(Boolean);
}

/** 从模特 slots 抽出可备份的图片列表 */
export function collectSourcesFromModelSlots(slots) {
  if (!slots || typeof slots !== 'object') return [];
  const out = [];
  Object.values(slots).forEach((value) => {
    const list = Array.isArray(value) ? value : (value && typeof value === 'object' ? [value] : []);
    list.forEach((item) => {
      const normalized = normalizeBackupSourceItem(item);
      if (normalized) out.push(normalized);
    });
  });
  return out;
}

/**
 * 预填备份：应对 lazy 工作室挂载前 Bootstrap 已 consume handoff。
 * 同时支持 sources（图片页）与 payload（模特 / 主图参数 / AI 文案等完整对象）。
 * @param {string} navId
 * @param {object[]|null|undefined} sources
 * @param {object|null|undefined} [payload]
 */
export function stashStudioPrefillBackup(navId, sources, payload) {
  const key = prefillBackupKey(navId);
  if (!key || typeof sessionStorage === 'undefined') return;
  const existing = readPrefillBackupRecord(navId);
  const list = normalizeBackupSources(sources);
  const nextSources = list.length > 0
    ? list
    : normalizeBackupSources(existing?.sources);
  const nextPayload = payload && typeof payload === 'object'
    ? payload
    : (existing?.payload && typeof existing.payload === 'object' ? existing.payload : null);
  if (nextSources.length === 0 && !nextPayload) return;
  try {
    sessionStorage.setItem(
      key,
      JSON.stringify({
        sources: nextSources,
        ...(nextPayload ? { payload: nextPayload } : {}),
        exp: Date.now() + PREFILL_BACKUP_TTL_MS,
      }),
    );
  } catch {
    /* quota */
  }
}

/**
 * @param {string} navId
 * @returns {object[]|null}
 */
export function readStudioPrefillBackup(navId) {
  const parsed = readPrefillBackupRecord(navId);
  if (!parsed) return null;
  const list = normalizeBackupSources(parsed.sources);
  if (list.length > 0) return list;
  // 模特等仅有 payload.slots 时，从 slots 还原图片列表
  if (parsed.payload?.slots) {
    const fromSlots = collectSourcesFromModelSlots(parsed.payload.slots);
    return fromSlots.length > 0 ? fromSlots : null;
  }
  if (Array.isArray(parsed.payload?.sources)) {
    const fromPayload = normalizeBackupSources(parsed.payload.sources);
    return fromPayload.length > 0 ? fromPayload : null;
  }
  return null;
}

/**
 * @param {string} navId
 * @returns {object|null}
 */
export function readStudioPrefillPayload(navId) {
  const parsed = readPrefillBackupRecord(navId);
  if (!parsed?.payload || typeof parsed.payload !== 'object') return null;
  return parsed.payload;
}

/** @param {string} navId */
export function clearStudioPrefillBackup(navId) {
  const key = prefillBackupKey(navId);
  if (!key || typeof sessionStorage === 'undefined') return;
  try {
    sessionStorage.removeItem(key);
  } catch {
    /* ignore */
  }
}

/**
 * Bootstrap consume handoff 当下立刻写入本标签 session 备份。
 * 工作台是 lazy 的，工作室挂载时 localStorage handoff 往往已被消费，必须靠这份备份首屏恢复。
 * 须同时备份「图片 sources」与「完整 payload」（模特 slots / 主图 studioParams / AI 文案参数）。
 * @param {Record<string, unknown>|null|undefined} handoffState
 */
export function stashHandoffStudioPrefillBackups(handoffState) {
  if (!handoffState || typeof handoffState !== 'object') return;
  const navId = String(handoffState.activeNav || '').trim();

  const stashSources = (id, sources, payload) => {
    const list = Array.isArray(sources) ? sources : [];
    if (!id) return;
    if (list.length === 0 && !(payload && typeof payload === 'object')) return;
    stashStudioPrefillBackup(id, list, payload);
  };

  if (Array.isArray(handoffState.cropSources) && handoffState.cropSources.length > 0) {
    stashSources(navId || 'smartCutout', handoffState.cropSources);
  }

  const goodsPrefill = handoffState.goodsStudioPrefill;
  if (goodsPrefill && typeof goodsPrefill === 'object') {
    stashSources(navId || 'goods', goodsPrefill.sources, goodsPrefill);
  }

  if (Array.isArray(handoffState.podStampSources) && handoffState.podStampSources.length > 0) {
    stashSources(navId || 'podProductDesign', handoffState.podStampSources);
  }

  const aiPrefill = handoffState.aiImageStudioPrefill;
  if (aiPrefill && typeof aiPrefill === 'object') {
    stashSources(
      String(aiPrefill.navId || navId || 'aiOutpaint'),
      aiPrefill.sources,
      aiPrefill,
    );
  }

  const modelPrefill = handoffState.modelStudioPrefill;
  if (modelPrefill && typeof modelPrefill === 'object') {
    stashSources(
      String(modelPrefill.navId || navId || 'modelTryOn'),
      collectSourcesFromModelSlots(modelPrefill.slots),
      modelPrefill,
    );
  }

  const videoPrefill = handoffState.videoStudioPrefill;
  if (videoPrefill && typeof videoPrefill === 'object') {
    stashSources(navId || 'videos', videoPrefill.sources, videoPrefill);
  }

  if (Array.isArray(handoffState.fissionSources) && handoffState.fissionSources.length > 0) {
    stashSources(navId || 'imageFission', handoffState.fissionSources);
  }
  if (Array.isArray(handoffState.imageIds) && handoffState.imageIds.length > 0) {
    const urlMap = handoffState.img2imgRefUrls && typeof handoffState.img2imgRefUrls === 'object'
      ? handoffState.img2imgRefUrls
      : {};
    const sources = handoffState.imageIds
      .map((id) => {
        const key = String(id);
        const url = String(urlMap[key] || urlMap[id] || '').trim();
        if (!url) return null;
        return { id: key, name: `参考_${key}`, url };
      })
      .filter(Boolean);
    stashSources(navId || 'img2img', sources);
  }
  if (Array.isArray(handoffState.imageUrls) && handoffState.imageUrls.length > 0) {
    stashSources(
      navId || 'infringementDetection',
      handoffState.imageUrls
        .map((url, index) => {
          const u = String(url || '').trim();
          return u ? { id: `url-${index}`, url: u, name: `检测图-${index + 1}` } : null;
        })
        .filter(Boolean),
    );
  }
}

/**
 * @param {string} url
 * @param {string} token
 */
export function appendWorkbenchHandoffQuery(url, token) {
  const raw = String(url || '').trim() || '/ipe';
  const abs = new URL(raw, typeof window !== 'undefined' ? window.location.origin : 'http://local');
  abs.searchParams.set(WORKBENCH_HANDOFF_QUERY, String(token));
  return `${abs.pathname}${abs.search}${abs.hash}`;
}

/**
 * 新标签打开工作台路径，并通过 handoff 传递 React Router state。
 * @param {string} url
 * @param {Record<string, unknown>} [state]
 */
export function openWorkbenchUrlWithState(url, state = {}) {
  const token = stashWorkbenchNavHandoff(state);
  openUrlInNewTab(appendWorkbenchHandoffQuery(url, token));
}

/**
 * @param {string} path 如 `/ipe/goods-detail/xxx`
 * @param {Record<string, unknown>} [state]
 */
export function openWorkbenchPathWithState(path, state = {}) {
  openWorkbenchUrlWithState(path, state);
}
